import {
  applicaImportHaccpAgent,
  verificaImportHaccpAgent,
} from '../../../../utils/api/aggiornamentoGestioneOperativaApi';
import { isCodiceErrore } from '../../../../utils/api/api';
import leggiTesto from './leggiTesto';

// The kind of defect each blocking finding of the backend names, as the
// operator reads it above the finding's own sentence.
const TIPI_DIFETTO = {
  GEOP_VERSIONE_SCONOSCIUTA: 'Versione dello schema sconosciuta',
  GEOP_ID_NON_VALIDO: 'Identificativo non valido',
  GEOP_ID_DUPLICATO: 'Identificativo ripetuto',
  GEOP_RIFERIMENTO_MANCANTE: 'Riferimento inesistente',
  GEOP_RIFERIMENTO_DUPLICATO: 'Riferimento ripetuto',
  GEOP_VALORE_FUORI_CATALOGO: 'Valore fuori catalogo',
  GEOP_VALORE_NON_NUMERICO: 'Valore non numerico',
  GEOP_CAMPO_OBBLIGATORIO: 'Campo obbligatorio mancante',
  GEOP_REPARTO_DEFAULT: 'Reparto predefinito',
  GEOP_NOME_DUPLICATO: 'Nome ripetuto',
  GEOP_NC_SEMPLIFICATA: 'Non conformità semplificata',
  GEOP_NC_INCOMPLETA: 'Non conformità incompleta',
  GEOP_CONTROLLO_BLOCCANTE_SENZA_CADENZA: 'Controllo bloccante senza cadenza',
  GEOP_CADENZA_INCOMPLETA: 'Cadenza incompleta',
  GEOP_GIORNO_INESISTENTE: 'Giorno inesistente',
  GEOP_CADENZA_IN_CONFLITTO: 'Cadenze in conflitto',
  GEOP_OLIO_FRIGGITRICE_CADENZE: "Orari dell'olio friggitrice",
  GEOP_TEMPERATURE_INCOMPLETE: 'Temperature incomplete',
  GEOP_LIMITI_MANCANTI: 'Limiti mancanti',
  GEOP_LIMITI_INCOERENTI: 'Limiti incoerenti',
  GEOP_CATEGORIA_PERSONALIZZATA: 'Categoria personalizzata',
  GEOP_TROPPE_COLONNE: 'Troppe colonne',
  GEOP_OPZIONI_COLONNA: 'Opzioni della colonna',
  GEOP_TESTO_TROPPO_LUNGO: 'Testo troppo lungo',
};

// A defect whose code has no type of its own, or that arrived as a sentence.
const TIPO_GENERICO = 'Elemento non conforme';

const fileNonConforme = (errori) =>
  Object.assign(new Error('Il file non è conforme.'), { errori });

export const difettoDaRilievo = ({ codice, elemento, messaggio }) => ({
  tipo: TIPI_DIFETTO[codice] || TIPO_GENERICO,
  descrizione: elemento ? `${elemento}: ${messaggio}` : messaggio,
});

/**
 * A 400 at the check is a file the backend could not read as an Export JSON:
 * a header field missing, a body it cannot map. Findings about the content
 * come in the check's 200 instead. Its `details` name the JSON fields in the
 * words of Bean Validation, in the language of the container, so the screen
 * says what to do instead. Any other failure stays the error it was.
 */
const formaNonRiconosciuta = (error) => {
  if (error?.status === 400) {
    throw fileNonConforme([
      {
        tipo: 'Formato non riconosciuto',
        descrizione:
          "Il file non ha la forma dell'Export JSON di HACCP Agent: riesportalo da HACCP Agent e riprova.",
      },
    ]);
  }
  throw error;
};

/**
 * A 400 at the write is the backend refusing a file the check had let
 * through: its `details` are its blocking findings, one sentence each. It
 * reaches the modal as a refused file; any other failure, or a 400 with
 * nothing to show, stays the error it was.
 */
const rifiutoComeDifetti = (error) => {
  const frasi = (error?.status === 400 ? (error.details ?? []) : []).filter(
    (dettaglio) =>
      typeof dettaglio === 'string' &&
      dettaglio.trim() &&
      !isCodiceErrore(dettaglio),
  );
  if (frasi.length > 0) {
    throw fileNonConforme(
      frasi.map((descrizione) => ({ tipo: TIPO_GENERICO, descrizione })),
    );
  }
  throw error;
};

// Read here as well as by the backend: a file that is not JSON gets a sentence
// the operator can act on instead of the parser's.
const verificaJson = (contenuto) => {
  try {
    JSON.parse(contenuto);
  } catch {
    throw fileNonConforme([
      {
        tipo: 'File non leggibile',
        descrizione:
          'Il contenuto non è un JSON valido: il file potrebbe essere troncato o corrotto.',
      },
    ]);
  }
};

// A warning reads as the element it is about and what to complete in it.
const fraseAvviso = ({ elemento, messaggio }) =>
  elemento ? `${elemento}: ${messaggio}` : messaggio;

/**
 * The `verifica` and `applica` of the import modal, as `useFlussoImportHaccp.js`
 * in this folder describes them, over the backend routes of this GeOp.
 *
 * The check refuses the file on any blocking finding, and lets warnings
 * through to the recap, which shows them before the write. The recap names the
 * workspace and how many procedure and schede of the GeOp the import replaces,
 * all three as the backend read them: the workspace is not read from the file,
 * whose header names that field differently depending on who wrote it. The
 * header's author is not shown: BS-FOOD keeps it without showing it.
 */
const importHaccpAgentBackend = (aggiornamentoGestioneOperativaId) => ({
  verifica: async (file, { signal } = {}) => {
    const contenuto = await leggiTesto(file);
    verificaJson(contenuto);
    const { rilievi, workspace, procedureDaSostituire, schedeDaSostituire } =
      await verificaImportHaccpAgent(
        aggiornamentoGestioneOperativaId,
        contenuto,
        { signal },
      ).catch(formaNonRiconosciuta);
    const bloccanti = rilievi.filter(({ gravita }) => gravita === 'BLOCCANTE');
    if (bloccanti.length > 0) {
      throw fileNonConforme(bloccanti.map(difettoDaRilievo));
    }
    return {
      riepilogo: {
        workspace,
        procedureDaSostituire,
        schedeDaSostituire,
        // The same sentence twice says nothing more.
        avvisi: [
          ...new Set(
            rilievi
              .filter(({ gravita }) => gravita === 'AVVISO')
              .map(fraseAvviso),
          ),
        ],
      },
      contenuto,
    };
  },
  applica: async (contenuto) => {
    await applicaImportHaccpAgent(
      aggiornamentoGestioneOperativaId,
      contenuto,
    ).catch(rifiutoComeDifetti);
  },
});

export default importHaccpAgentBackend;
